import { Injectable } from "@angular/core";
import { environment } from "../../environments/environment";
import { EventService } from "./event.service";
import { RudderStackService } from "./rudderstack.service";

/**
 * Customer.io In-App messaging integration (Angular port of the proven
 * egmat-public-site React implementation).
 *
 * Design goals (intentionally additive / zero side-effects on the existing system):
 *  - Loads Customer.io's CDP snippet under the `cioanalytics` global ONLY, so it
 *    never collides with any other analytics global.
 *  - It is fully gated behind environment.customerIoCdpKey. With no key set, every
 *    method here is a complete no-op (ship-dark safe).
 *  - Identity comes from the SAME source RudderStack identifies from (the app's
 *    userLoginCookie, via RudderStackService.identifiedEmail). Until 2026-09-07 it
 *    was mirrored off Segment's window.analytics through a source middleware; the
 *    CDN script that loaded Segment is gone, so that hook had nothing to attach to.
 *  - We do NOT mirror business `track()` events into cioanalytics (RudderStack
 *    already feeds Customer.io in cloud-mode). We only send identify + page(), plus
 *    re-emit in-app INTERACTION events through EventService so they land in the
 *    normal analytics.
 *
 * Achieves all three requirements:
 *   1) In-app pop-ups for logged-in users   -> identify() on init
 *   2) In-app pop-ups for anonymous users   -> anonymousInApp: true
 *   3) Fire interaction events               -> events() callback -> EventService.track()
 */
@Injectable()
export class CustomerIoService {
  private initialized = false;

  constructor(
    private eventService: EventService,
    private rudderStackService: RudderStackService,
  ) {}

  /**
   * messageId -> human-readable flow name. The SDK only sends the gibberish
   * messageId on viewed/dismissed/interacted events (no name), so we resolve the
   * name here so EVERY event carries a readable flow_name. Populate it directly
   * below, or load it at runtime via setFlowNameMap() from app config so adding a
   * new flow needs no code deploy.
   */
  private flowNames: { [messageId: string]: string } = {
    // "abc123def456": "Retention Circle Welcome",
  };

  /** Optionally load the messageId->name map at runtime (e.g. from app-settings). */
  setFlowNameMap(map: { [messageId: string]: string }): void {
    if (map) {
      this.flowNames = map;
    }
  }

  private get cdpKey(): string {
    return (environment as any).customerIoCdpKey || "";
  }
  private get siteId(): string {
    return (environment as any).customerIoSiteId || "";
  }
  private get region(): string {
    return ((environment as any).customerIoRegion || "us").toLowerCase();
  }
  private get snippetHost(): string {
    return this.region === "eu"
      ? "https://cdp-eu.customer.io"
      : "https://cdp.customer.io";
  }
  private get cio(): any {
    return (window as any).cioanalytics;
  }

  /**
   * Entry point. Call once on app start (from AppComponent). No-op if the CDP key
   * is absent. Loads the snippet and identifies the logged-in user (if any), but
   * does NOT fire the initial page() — that is owned by the router NavigationEnd
   * subscription so we never double-fire on first load.
   */
  init(): void {
    if (this.initialized || typeof window === "undefined") {
      return;
    }
    if (!this.cdpKey) {
      return; // not configured -> complete no-op
    }
    // Admin "Login as User" session: load nothing, for the same reason
    // RudderStackService.init() bails out - an admin's clicks are not the
    // student's behaviour, and identifying here would attach the admin's browser
    // to the student's profile. Gated on THAT service's check so the two clients
    // can never disagree about whether the session is a real student's.
    if (this.rudderStackService.isImpersonatedSession()) {
      return;
    }
    this.initialized = true;
    try {
      this.loadSnippet();
    } catch (e) {
      console.warn("[CustomerIo] failed to load snippet:", e);
    }
    try {
      this.identify();
    } catch (e) {
      console.warn("[CustomerIo] failed to identify:", e);
    }
  }

  /**
   * Identify the logged-in user to Customer.io. Safe to call more than once
   * (e.g. after an in-app login writes userLoginCookie). No-op for anonymous
   * visitors: identifying them would mint a profile per visitor.
   *
   * The `cioanalytics` stub queues calls made before the real library loads,
   * so this needs no polling - it can run right after loadSnippet().
   */
  identify(identityOverride?: string): void {
    if (!this.cdpKey || typeof window === "undefined") {
      return;
    }
    try {
      const email = this.rudderStackService.identifiedEmail(identityOverride);
      const cio = this.cio;
      if (email && cio && typeof cio.identify === "function") {
        cio.identify(email, { email: email });
      }
    } catch (e) {
      console.warn("[CustomerIo] identify failed:", e);
    }
  }

  /**
   * SPA requirement: Customer.io needs a manual page() on every route change
   * (and once initially). Wired to Router NavigationEnd in AppComponent.
   */
  page(): void {
    if (!this.cdpKey || typeof window === "undefined") {
      return;
    }
    const cio = this.cio;
    if (cio && typeof cio.page === "function") {
      cio.page();
    }
  }

  /**
   * Clear Customer.io identity on logout to prevent identity bleed on shared
   * devices. Replaces the legacy deleteCookie("_cio") relic.
   */
  reset(): void {
    if (typeof window === "undefined") {
      return;
    }
    const cio = this.cio;
    if (cio && typeof cio.reset === "function") {
      cio.reset();
    }
  }

  /**
   * Re-emit Customer.io in-app lifecycle events into the analytics pipeline
   * (EventService -> RudderStack). Fires four events — in-app-flow-viewed / -dismissed / -interacted /
   * -error — and ignores any other SDK event type.
   *
   * flow_name is attached to EVERY event via the messageId->name map (flowNames),
   * because the SDK only sends the messageId on viewed/dismissed. The button
   * fields (action_name / action_value) only exist on the interaction event, so
   * they are added only there.
   */
  private handleInAppEvent(event: any): void {
    try {
      const detail = (event && event.detail) || {};
      const messageId = detail.messageId || "";
      const base: any = {
        flow_id: messageId,
        flow_name: this.flowNames[messageId] || "",
        delivery_id: detail.deliveryId || "",
        eg_timestamp: new Date().toISOString(),
      };

      switch (event && event.type) {
        case "in-app:message-opened":
          this.eventService.track("in-app-flow-viewed", base);
          break;
        case "in-app:message-dismissed":
          this.eventService.track("in-app-flow-dismissed", base);
          break;
        case "in-app:message-error":
          this.eventService.track("in-app-flow-error", base);
          break;
        case "in-app:message-action":
          this.eventService.track(
            "in-app-flow-interacted",
            Object.assign({}, base, {
              // "which button / action was clicked" + its configured value.
              // Both are set per-action in the Customer.io message template.
              action_name: detail.actionName || "",
              action_value: detail.actionValue || "",
            })
          );
          break;
        default:
          break;
      }
    } catch (e) {
      // Never let analytics handling surface to the user.
      console.warn("[CustomerIo] in-app event handler error:", e);
    }
  }

  /**
   * Official Customer.io CDP loader stub, rewritten readably so we can inject the
   * In-App Plugin config (anonymousInApp + events callback) cleanly. Namespaced as
   * the `cioanalytics` global so it cannot collide with another analytics global.
   */
  private loadSnippet(): void {
    const globalKey = "cioanalytics";
    const w = window as any;
    const analytics = (w[globalKey] = w[globalKey] || []);

    // Real library already present, or stub already created -> do nothing.
    if (analytics.initialize) {
      return;
    }
    if (analytics.invoked) {
      console.error("[CustomerIo] Snippet included twice.");
      return;
    }
    analytics.invoked = true;
    analytics.methods = [
      "trackSubmit", "trackClick", "trackLink", "trackForm", "pageview",
      "identify", "reset", "group", "track", "ready", "alias", "debug",
      "page", "once", "off", "on", "addSourceMiddleware",
      "addIntegrationMiddleware", "setAnonymousId", "addDestinationMiddleware",
    ];
    analytics.factory = function (method: string) {
      return function () {
        const args = Array.prototype.slice.call(arguments);
        args.unshift(method);
        analytics.push(args);
        return analytics;
      };
    };
    for (let i = 0; i < analytics.methods.length; i++) {
      const key = analytics.methods[i];
      analytics[key] = analytics.factory(key);
    }
    analytics.load = function (writeKey: string, options: any) {
      const script = document.createElement("script");
      script.type = "text/javascript";
      script.async = true;
      script.setAttribute("data-global-customerio-analytics-key", globalKey);
      script.src =
        this._host + "/v1/analytics-js/snippet/" + writeKey + "/analytics.min.js";
      const first = document.getElementsByTagName("script")[0];
      first.parentNode.insertBefore(script, first);
      analytics._writeKey = writeKey;
      analytics._loadOptions = options;
    };
    // expose host on the stub so the closure above can read it
    analytics._host = this.snippetHost;
    analytics.SNIPPET_VERSION = "4.15.3";

    const inAppConfig: any = {
      anonymousInApp: true, // requirement #2: show to anonymous visitors
      events: (event: any) => this.handleInAppEvent(event), // requirement #3
    };
    // siteId is optional in the loader but recommended; only include when provided.
    if (this.siteId) {
      inAppConfig.siteId = this.siteId;
    }

    analytics.load(this.cdpKey, {
      integrations: {
        "Customer.io In-App Plugin": inAppConfig,
      },
    });
    // NOTE: initial page() is intentionally handled by the router subscription,
    // not here, to avoid a duplicate page() call on first load.
  }
}
